import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WalletInfo, TokenBalance, TransactionHistory } from '../types/blockchain';

interface Web3ContextType {
  // 钱包状态
  wallet: WalletInfo | null;
  isConnecting: boolean;
  
  // 钱包操作
  connectWallet: () => Promise<void>;
  disconnectWallet: () => void;
  switchNetwork: (chainId: string) => Promise<void>;
  
  // 余额和交易
  tokenBalances: TokenBalance[];
  transactionHistory: TransactionHistory[];
  refreshBalances: () => Promise<void>;
  
  // 合约交互
  signMessage: (message: string) => Promise<string>;
  sendTransaction: (to: string, value: string, data?: string) => Promise<string>;
}

const Web3Context = createContext<Web3ContextType | undefined>(undefined);

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (context === undefined) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

interface Web3ProviderProps {
  children: ReactNode;
}

export const Web3Provider: React.FC<Web3ProviderProps> = ({ children }) => {
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>([]);
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistory[]>([]);

  // Mock 钱包连接
  const connectWallet = async () => {
    setIsConnecting(true);
    try {
      // 模拟连接延迟
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockWallet: WalletInfo = {
        address: 'DsVmA5hWGAnP2FADiN1y8NxjqRNMxcxGMSTdEtVo9BV9',
        balance: '1.2345',
        network: 'Solana',
        isConnected: true
      };
      
      setWallet(mockWallet);
      localStorage.setItem('web3_wallet', JSON.stringify(mockWallet));
      
      // 加载 mock 数据
      await refreshBalances();
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setWallet(null);
    setTokenBalances([]);
    setTransactionHistory([]);
    localStorage.removeItem('web3_wallet');
  };

  const switchNetwork = async (chainId: string) => {
    // Mock 网络切换
    if (wallet) {
      const networkName = chainId === 'mainnet-beta' ? 'Solana' : 'Solana Devnet';
      setWallet({ ...wallet, network: networkName });
    }
  };

  const refreshBalances = async () => {
    if (!wallet) return;
    
    // Mock token balances
    const mockBalances: TokenBalance[] = [
      {
        symbol: 'SOL',
        balance: '1.2345',
        decimals: 9,
        usdValue: '185.00'
      },
      {
        symbol: 'IBOM',
        balance: '1000.0',
        decimals: 9,
        usdValue: '500.00'
      },
      {
        symbol: 'USDC',
        balance: '250.50',
        decimals: 6,
        usdValue: '250.50'
      }
    ];
    
    setTokenBalances(mockBalances);
    
    // Mock transaction history
    const mockHistory: TransactionHistory[] = [
      {
        hash: '0x1234...abcd',
        type: 'copyright_register',
        status: 'confirmed',
        timestamp: Date.now() - 3600000,
        gasUsed: '21000',
        gasFee: '0.001',
        details: { workTitle: 'My Song' }
      },
      {
        hash: '0x5678...efgh',
        type: 'nft_mint',
        status: 'confirmed',
        timestamp: Date.now() - 7200000,
        gasUsed: '85000',
        gasFee: '0.005',
        details: { tokenId: '123' }
      }
    ];
    
    setTransactionHistory(mockHistory);
  };

  const signMessage = async (message: string): Promise<string> => {
    // Mock 签名
    await new Promise(resolve => setTimeout(resolve, 1000));
    return `0x${'a'.repeat(130)}`;
  };

  const sendTransaction = async (to: string, value: string, data?: string): Promise<string> => {
    // Mock 交易发送
    await new Promise(resolve => setTimeout(resolve, 2000));
    return `0x${'1'.repeat(64)}`;
  };

  // 初始化时检查本地存储
  useEffect(() => {
    const savedWallet = localStorage.getItem('web3_wallet');
    if (savedWallet) {
      const walletInfo = JSON.parse(savedWallet);
      setWallet(walletInfo);
      refreshBalances();
    }
  }, []);

  return (
    <Web3Context.Provider
      value={{
        wallet,
        isConnecting,
        connectWallet,
        disconnectWallet,
        switchNetwork,
        tokenBalances,
        transactionHistory,
        refreshBalances,
        signMessage,
        sendTransaction,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};
